import authStorage from "src/utils/authStorage";
/* eslint-disable react-hooks/exhaustive-deps */

import moment from "moment";
import React, { createContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "../lib/toast";
import api from "../utils/api";
import unmask from "../utils/unmask";

export const AuthContext = createContext();

// Dono (partner) e membro convidado (partnerUser) compartilham o painel do parceiro.
const isPartnerType = (type) => type === "partner" || type === "partnerUser";

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState("");
  const [salesAuthData, setSalesAuthData] = useState("");
  const [loading, setLoading] = useState(true);
  const { pathname } = useLocation();
  const isSalesUser = pathname.startsWith("/vendas");

  useEffect(() => {
    const token = authStorage.getItem("@sacgpt:token");
    const userStorage = authStorage.getItem("@sacgpt:user");
    const forcedRefresh = localStorage.getItem("@sacgpt:forcedRefresh");

    if (userStorage) {
      const userStorageParsed = JSON.parse(userStorage);
      setAuthData(userStorageParsed);

      // Só executa refreshUserPermissions para partners/membros, não para admin
      if (isPartnerType(userStorageParsed.userType)) {
        if (forcedRefresh === "true") {
          localStorage.removeItem("@sacgpt:forcedRefresh");
          setTimeout(() => {
            refreshUserPermissions();
          }, 1000);
        } else if (
          userStorageParsed.userGroupId &&
          (!userStorageParsed.userGroup || !userStorageParsed.userGroup.roles)
        ) {
          setTimeout(() => {
            refreshUserPermissions();
          }, 1000);
        }
      }
    }

    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const salesToken = localStorage.getItem("@sacgpt:sales-token");
    const salesUser = localStorage.getItem("@sacgpt:sales-user");

    if (salesUser) {
      const salesUserParsed = JSON.parse(salesUser);
      setSalesAuthData(salesUserParsed);
    }

    if (salesToken) {
      api.defaults.headers.Authorization = `Bearer ${salesToken}`;
    }

    setLoading(false);
  }, []);

  // Função updateUserPartner removida - não estava sendo usada

  // Load user from localStorage - useful after signup when we don't want to trigger re-renders
  const loadUserFromStorage = () => {
    const token = authStorage.getItem("@sacgpt:token");
    const userStorage = authStorage.getItem("@sacgpt:user");

    if (userStorage && token) {
      const userStorageParsed = JSON.parse(userStorage);
      api.defaults.headers.Authorization = `Bearer ${token}`;
      setAuthData(userStorageParsed);
      return true;
    }
    return false;
  };

  const updateUser = async (data) => {
    const user = data.data.attributes;
    const currentUser = JSON.parse(authStorage.getItem("@sacgpt:user") || "{}");
    const currentUserSales = JSON.parse(localStorage.getItem("@sacgpt:sales-user") || "{}");

    const resolvedUserType =
      user.userType || currentUser.userType || currentUserSales.userType || authData?.userType; // fallback final

    const updatedUserData = {
      ...user,
      id: data.data.id,
      authToken: authData?.authToken,
      expiresAt: authData?.expiresAt,
      userType: resolvedUserType,
      // Map WhatsApp connection status
      whatsappConnected: user.wppConnected === true || user.metaWppConnected === true || false,
    };

    if (resolvedUserType === "sales") {
      localStorage.setItem("@sacgpt:sales-user", JSON.stringify(updatedUserData));
      setSalesAuthData(updatedUserData);
    } else {
      authStorage.setItem("@sacgpt:user", JSON.stringify(updatedUserData));
      setAuthData(updatedUserData);
    }
  };

  const signIn = async ({ email, password, rememberMe = true }) => {
    try {
      const { data } = await api.post(`/partners/authenticate`, {
        email: email.toLowerCase().trim(),
        password: password.trim(),
      });
      const user = data.data.attributes;
      user.id = data.data.id;

      if (user.authToken) {
        authStorage.setPersistence(rememberMe);
        api.defaults.headers.Authorization = `Bearer ${user.authToken}`;
        authStorage.setItem("@sacgpt:token", user.authToken);
        // Para membro (partnerUser), data.data.id é o id do próprio membro; o partner
        // real vem no atributo partnerId do serializer. Para o dono, id === partnerId.
        const isMember = data.data.type === "partnerUser";
        const sessionUser = {
          ...user,
          id: data.data.id,
          partnerId: isMember ? user.partnerId : data.data.id,
          whatsappConnected: user.wppConnected === true || user.metaWppConnected === true || false,
          userType: data.data.type,
        };
        authStorage.setItem("@sacgpt:user", JSON.stringify(sessionUser));
        setAuthData(sessionUser);
      } else {
        throw new Error("Token not found");
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  const signInAdmin = async ({ email, password }) => {
    try {
      const { data } = await api.post(`/admin/authenticate`, {
        email: email.toLowerCase().trim(),
        password: password.trim(),
      });
      const user = data.data.attributes;
      user.id = data.data.id;

      if (user.authToken) {
        authStorage.setPersistence(true);
        // Adding it's token to all requests for now on.
        api.defaults.headers.Authorization = `Bearer ${user.authToken}`;
        authStorage.setItem("@sacgpt:token", user.authToken);
        authStorage.setItem(
          "@sacgpt:user",
          JSON.stringify({ ...user, id: data.data.id, userType: "admin" }),
        );
        setAuthData({ ...user, id: data.data.id, userType: "admin" });
      } else {
        throw new Error("Token not found");
      }
    } catch (error) {
      //setAuthData("teste");
      throw new Error(error);
    }
  };

  const signInSales = async ({ email, password }) => {
    try {
      const { data } = await api.post(`/affiliates/authenticate`, {
        email: email.toLowerCase().trim(),
        password: password.trim(),
      });

      const user = data.data.attributes;
      user.id = data.data.id;

      if (user.authToken) {
        // Adding it's token to all requests for now on.
        api.defaults.headers.Authorization = `Bearer ${user.authToken}`;
        localStorage.setItem("@sacgpt:sales-token", user.authToken);
        localStorage.setItem(
          "@sacgpt:sales-user",
          JSON.stringify({ ...user, id: user.id, userType: "sales" }),
        );
        setSalesAuthData({ ...user, id: user.id, userType: "sales" });
      } else {
        throw new Error("Token not found");
      }
    } catch (error) {
      //setAuthData("teste");
      throw new Error(error);
    }
  };

  const signInWithGoogle = async (credential, { rememberMe = true } = {}) => {
    try {
      const { data } = await api.post("/partners/google_auth", {
        credential: credential,
      });

      const user = data.data.attributes;
      user.id = data.data.id;

      // Check if this is a new registration from meta
      const isNewRegistration = data.meta?.isNewRegistration || false;
      const phoneRequired = data.meta?.phoneRequired || false;

      if (user.authToken) {
        authStorage.setPersistence(rememberMe);
        // If new registration, store flag in sessionStorage FIRST (before any state changes)
        if (isNewRegistration) {
          const registrationData = {
            partnerName: user.name,
            timestamp: Date.now(),
          };
          sessionStorage.setItem("@sacgpt:newRegistration", JSON.stringify(registrationData));
        }

        // Adding it's token to all requests for now on.
        api.defaults.headers.Authorization = `Bearer ${user.authToken}`;
        authStorage.setItem("@sacgpt:token", user.authToken);
        authStorage.setItem(
          "@sacgpt:user",
          JSON.stringify({
            ...user,
            id: user.id,
            partnerId: user.id,
            userType: "partner",
            whatsappConnected:
              user.wppConnected === true || user.metaWppConnected === true || false,
          }),
        );

        // Only call setAuthData if NOT a new registration to avoid race condition
        // For new registrations, Login.js will navigate to /obrigado, and ThankYou page
        // will call loadUserFromStorage() before redirecting to dashboard
        if (!isNewRegistration) {
          setAuthData({
            ...user,
            id: user.id,
            partnerId: user.id,
            userType: "partner",
            whatsappConnected:
              user.wppConnected === true || user.metaWppConnected === true || false,
          });
        }

        toast.success(
          isNewRegistration ? "Cadastro realizado com sucesso!" : "Login realizado com sucesso!",
        );

        // Store phone required flag if needed
        if (phoneRequired) {
          sessionStorage.setItem("@sacgpt:phoneRequired", "true");
        }

        // Return info about registration status for the caller to handle navigation
        return { isNewRegistration, phoneRequired, partnerName: user.name };
      } else {
        throw new Error("Token not found");
      }
    } catch (error) {
      console.error("Erro no login com Google:", error);
      throw new Error(error);
    }
  };

  const updatePhoneNumber = async (phoneNumber) => {
    try {
      const { data } = await api.patch("/partners/update_phone", {
        contact_number: phoneNumber,
      });

      const user = data.data.attributes;
      user.id = data.data.id;

      // Update local storage and state with new phone number
      const currentUser = JSON.parse(authStorage.getItem("@sacgpt:user"));
      const updatedUser = {
        ...currentUser,
        ...user,
        id: data.data.id,
        partnerId: data.data.id,
        contactNumber: user.contactNumber,
        whatsappConnected: user.wppConnected === true || user.metaWppConnected === true || false,
      };

      authStorage.setItem("@sacgpt:user", JSON.stringify(updatedUser));
      setAuthData(updatedUser);

      toast.success("Telefone atualizado com sucesso!");
      return updatedUser;
    } catch (error) {
      console.error("Erro ao atualizar telefone:", error);
      const errorMessage =
        error.response?.data?.error || "Erro ao atualizar telefone. Tente novamente.";
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signUp = async ({ values }) => {
    try {
      const { data } = await api.post("/partners/partners", {
        data: {
          attributes: {
            name: values.name.trim(),
            email: values.email.toLowerCase().trim(),
            contactNumber: unmask(values.phone.trim()),
            password: values.password.trim(),
            passwordConfirmation: values.passwordConfirmation.trim(),
          },
        },
      });

      if (data) {
        toast.success("Cadastro realizado com sucesso!");

        const user = data.data.attributes;
        user.id = data.data.id;

        if (user.authToken) {
        authStorage.setPersistence(true);
          // Store registration flag in sessionStorage FIRST (before any state changes)
          // PublicRoute will check this to redirect to /obrigado instead of /dashboard
          const registrationData = {
            partnerName: values.name,
            timestamp: Date.now(),
          };
          sessionStorage.setItem("@sacgpt:newRegistration", JSON.stringify(registrationData));

          api.defaults.headers.Authorization = `Bearer ${user.authToken}`;
          authStorage.setItem("@sacgpt:token", user.authToken);
          authStorage.setItem(
            "@sacgpt:user",
            JSON.stringify({
              ...user,
              id: data.data.id,
              partnerId: data.data.id,
              userType: data.data.type,
            }),
          );

          // DO NOT call setAuthData here to avoid race condition!
          // The user data is saved to localStorage. SignUp component will call navigate("/obrigado")
          // without triggering a re-render of PublicRoute (because user context is still empty).
          // ThankYou page will call loadUserFromStorage() to load the user before redirecting to dashboard.

          return "success";
        }
      }
    } catch (error) {
      if (error?.response?.status === 422) {
        if (
          [...(error?.response?.data?.errors || [])].some((item) => {
            return item?.title === "Email has already been taken";
          })
        ) {
          toast.error("O email inserido já está em uso.");
        } else toast.error("Ocorreu um erro ao tentar realizar o cadastro.");
      } else toast.error("Ocorreu um erro ao tentar realizar o cadastro.");

      throw new Error(error);
    }
  };

  const signUpSales = async ({ values }) => {
    try {
      const { data } = await api.post(`/affiliates/affiliates/`, {
        data: {
          attributes: {
            name: values.name.trim(),
            email: values.email.toLowerCase().trim(),
            contactNumber: unmask(values.phone.trim()),
            // serviceNumber: unmask(values.phone.trim()),
            password: values.password.trim(),
            passwordConfirmation: values.passwordConfirmation.trim(),
            document:
              values.documentType === "cpf"
                ? unmask(values.cpf)
                : values.documentType === "cnpj"
                  ? unmask(values.cnpj)
                  : null,
          },
        },
      });

      if (data) {
        toast.success("Cadastro realizado com sucesso!");

        const user = data.data.attributes;
        user.id = data.data.id;

        if (user.authToken) {
          // Adding it's token to all requests for now on.
          api.defaults.headers.Authorization = `Bearer ${user.authToken}`;
          localStorage.setItem("@sacgpt:sales-token", user.authToken);
          localStorage.setItem(
            "@sacgpt:sales-user",
            JSON.stringify({ ...user, id: data.data.id, userType: "sales" }),
          );
          setSalesAuthData({ ...user, id: data.data.id, userType: "sales" });

          return "success";
        }
      }
    } catch (error) {
      if (error?.response?.status === 422) {
        if (
          [...(error?.response?.data?.errors || [])].some((item) => {
            return item?.title === "Email has already been taken";
          })
        ) {
          toast.error("O email inserido já está em uso.");
        } else toast.error("Ocorreu um erro ao tentar realizar o cadastro.");
      } else toast.error("Ocorreu um erro ao tentar realizar o cadastro.");

      throw new Error(error);
    }
  };

  const updateAffiliated = async (data) => {
    const user = data.data.attributes;
    const currentUser = JSON.parse(localStorage.getItem("@sacgpt:sales-user") || "{}");

    const updatedUserData = {
      ...user,
      id: data.data.id,
      userType: "sales",
      authToken: currentUser.authToken || authData?.authToken,
      expiresAt: currentUser.expiresAt || authData?.expiresAt,
    };

    localStorage.setItem("@sacgpt:sales-user", JSON.stringify(updatedUserData));
    setSalesAuthData(updatedUserData);
  };

  const updatePartnerDetail = async (data) => {
    const currentUser = JSON.parse(authStorage.getItem("@sacgpt:user") || "{}");
    let updatedUserData = {};

    if (authData?.partner?.partnerDetailId) {
      updatedUserData = {
        ...authData,
        userType: currentUser.userType || authData.userType, // Preserva o userType existente
        partner: { ...authData.partner, partnerDetailId: data.data.id },
      };
    } else {
      updatedUserData = {
        ...authData,
        partnerDetailId: data.data.id,
        userType: currentUser.userType || authData.userType, // Preserva o userType existente
      };
    }

    authStorage.setItem("@sacgpt:user", JSON.stringify(updatedUserData));
    setAuthData(updatedUserData);
  };

  //TODO VERIFICAR ESSE FLUXO AQUI FUTURATMENE?

  const signOutIfTokenExpired = async () => {
    const userData = authStorage.getItem("@sacgpt:user");
    if (userData) {
      const parsedUser = JSON.parse(userData);

      // Admin users don't have expiresAt, so skip expiration check for them
      if (parsedUser.userType === "admin") {
        api.defaults.headers.Authorization = `Bearer ${parsedUser.authToken}`;

        // Also ensure the token is in localStorage
        if (parsedUser.authToken) {
          authStorage.setItem("@sacgpt:token", parsedUser.authToken);
        }

        setAuthData(parsedUser);
      } else if (parsedUser.expiresAt && moment(parsedUser.expiresAt).isBefore(new Date())) {
        signOut();
      } else {
        api.defaults.headers.Authorization = `Bearer ${parsedUser.authToken}`;
        setAuthData(parsedUser);
      }
    }

    const salesData = localStorage.getItem("@sacgpt:sales-user");
    if (salesData) {
      const parsedSalesUser = JSON.parse(salesData);
      if (parsedSalesUser.expiresAt && moment(parsedSalesUser.expiresAt).isBefore(new Date())) {
        signOut();
      } else {
        setSalesAuthData(parsedSalesUser);
      }
    }
  };

  useEffect(() => {
    signOutIfTokenExpired();

    const permissionsRefreshInterval = setInterval(() => {
      if (authData && isPartnerType(authData.userType) && authData.userGroupId) {
        refreshUserPermissions();
      }
    }, 300000);

    return () => clearInterval(permissionsRefreshInterval);
  }, []);

  useEffect(() => {
    // Só verifica WhatsApp para partners, não para admin
    if (authData && authData.userType === "partner") {
      const checkWhatsConeccted = async () => {
        try {
          const { data } = await api.get("/partners/partners/wpp_connected", {
            headers: {
              "Cache-Control": "no-cache",
            },
          });
          if (!data.wppConnected && authData?.partnerDetailId) {
            const { data } = await api.get(`partners/partners/${authData?.partnerDetailId}`);
            await updateUser(data);
          }
        } catch (error) {
          console.error("Erro ao verificar conexão do WhatsApp:", error);
        }
      };

      checkWhatsConeccted();
    }
  }, [authData?.partnerDetailId, authData?.userType]);

  const signOut = async () => {
    api.defaults.headers.Authorization = null;

    setAuthData("");
    if (isSalesUser) {
      setSalesAuthData("");
      localStorage.removeItem("@sacgpt:sales-token");
      localStorage.removeItem("@sacgpt:sales-user");
    }

    authStorage.clear();
  };

  if (loading) {
    return <h1>Loading ...</h1>;
  }

  // Função para recarregar as informações do usuário, incluindo permissões
  const refreshUserPermissions = async () => {
    try {
      setLoading(true);

      if (authData && isPartnerType(authData.userType) && authData.userGroupId) {
        // Busca as informações atualizadas do usuário
        const { data: userData } = await api.get(`/partners/partner_users/${authData.id}`);

        // Busca as informações atualizadas do grupo de usuário
        const { data: groupData } = await api.get(`/partners/user_groups/${authData.userGroupId}`);

        // Busca informações do parceiro para atualizar o checkList
        let partnerData = null;
        if (authData.partnerId) {
          try {
            const response = await api.get(`partners/partners/${authData.partnerId}`);
            partnerData = response.data;
          } catch (error) {
            console.error("Erro ao buscar dados do parceiro:", error);
          }
        } else {
        }

        // Obtém o userType atual do localStorage
        const currentUser = JSON.parse(authStorage.getItem("@sacgpt:user") || "{}");
        const currentUserType = currentUser.userType || authData.userType;

        // Atualiza o objeto de usuário com as informações atualizadas
        const updatedUser = {
          ...authData,
          ...userData.data.attributes,
          userGroup: groupData.data.attributes,
          // Garante que as propriedades do parceiro sejam incluídas
          profileFilled: partnerData?.data?.attributes?.profileFilled || authData.profileFilled,
          partnerDetailsFilled:
            partnerData?.data?.attributes?.partnerDetailsFilled || authData.partnerDetailsFilled,
          activePlan: partnerData?.data?.attributes?.activePlan || authData.activePlan,
          // Map WhatsApp connection status — strict boolean, no localStorage fallback
          // (fixes Issue F1: stale `true` from localStorage overriding fresh `false` from API)
          whatsappConnected:
            partnerData?.data?.attributes?.wppConnected === true ||
            partnerData?.data?.attributes?.metaWppConnected === true ||
            false,
          // Preserva o userType atual
          userType: currentUserType,
        };

        // Atualiza o localStorage e o estado
        authStorage.setItem("@sacgpt:user", JSON.stringify(updatedUser));
        setAuthData(updatedUser);

        // Limpa o cache do navegador para forçar a recarga das permissões
        localStorage.removeItem("@sacgpt:routePermissionsCache");

        toast.success("Permissões atualizadas com sucesso!");
      }
    } catch (error) {
      // Suppress 403 PHONE_REQUIRED - PhoneNumberModal handles it
      if (error?.response?.status === 403 && error?.response?.data?.code === "PHONE_REQUIRED")
        return;
      console.error("Erro ao atualizar permissões:", error);
      toast.error("Erro ao atualizar permissões. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const forceRefreshPermissions = () => {
    const token = authStorage.getItem("@sacgpt:token");

    authStorage.removeItem("@sacgpt:user");
    localStorage.clear();

    if (token) {
      authStorage.setItem("@sacgpt:token", token);
    }

    localStorage.setItem("@sacgpt:forcedRefresh", "true");

    // Recarrega a página
    window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{
        user: authData,
        userSales: salesAuthData,
        isSalesUser,
        loading,
        updateUser,
        updatePartnerDetail,
        updateAffiliated,
        signIn,
        signOut,
        signInAdmin,
        signInSales,
        signInWithGoogle,
        updatePhoneNumber,
        signUp,
        signUpSales,
        refreshUserPermissions, // Função para atualizar permissões
        forceRefreshPermissions, // Nova função para forçar a limpeza do cache e recarregar a página
        loadUserFromStorage, // Load user from localStorage after signup navigation
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
